// The wrapper itself, not the `@/features/analytics` barrel: the store layer
// (the make-repeatable thunk) imports this module too.
import { track } from "@/features/analytics/utils/mixpanel-actions.utils";
import {
  highestRequiredPlan,
  requiredPlanForVariable,
} from "../plan-rules/plan-rules";
import { parseDateToken } from "../date-variable/date-token";
import { tabForVariable } from "../variable-picker/hooks/use-recent-variables";
import type { PickerVariable } from "../variable-picker/types";

/**
 * Mixpanel events for Dynamic Variables. Names MUST stay lowercase and match
 * the tracking plan (Desktop/Storydoc/VARIABLES_ANALYTICS_PLAN.md): the
 * production `[Variables]` dashboards key off these exact strings, so a
 * rename silently empties a report. Front-end events only, by decision.
 */
export const VARIABLES_EVENTS = {
  OPEN_VARIABLE_PICKER: "open variable picker",
  OPEN_VARIABLE_TYPEAHEAD: "open variable typeahead",
  INSERT_VARIABLE: "insert variable",
  CLICK_LOCKED_VARIABLE: "click locked variable",
  SUCCESS_CREATE_VARIABLE: "success create variable",
  SUCCESS_IMPORT_VARIABLES_CSV: "success import variables csv",
  UNBIND_VARIABLE: "unbind variable",
  SUCCESS_CREATE_FORMULA: "success create formula",
  EDIT_DATE_VARIABLE: "edit date variable",
  SUCCESS_MAKE_REPEATABLE: "success make repeatable",
  SUCCESS_MAKE_OPTIONAL: "success make optional",
  UPGRADE_TO_PUBLISH_DIALOG: "upgrade to publish dialog",
  START_CREATE_VERSION: "start create version",
  EDIT_VARIABLE_VALUE: "edit variable value",
  GENERATE_AI_VARIABLE: "generate ai variable",
} as const;

export type InsertMethod = "picker" | "typeahead" | "drag and drop";

/** Element a variable landed on, as the plan names it (`RichText` → `text`). */
export const targetElementOf = (elementName?: string | null): string => {
  if (!elementName || elementName === "RichText") return "text";
  if (elementName === "Embedded") return "iframe";
  return elementName.toLowerCase();
};

/** `computed` is the picker's name for a formula; reports call it calculation. */
export const variableTypeOf = (type?: string | null): string =>
  type === "computed" ? "calculation" : type || "input";

const bareName = (token: string): string =>
  token.replace(/^\{\{\{?\s*/, "").replace(/\s*\}?\}\}$/, "");

/** Fires `insert variable` for every path a variable lands by. */
export const trackInsertVariable = ({
  variable,
  method,
  targetElement,
}: {
  variable: PickerVariable;
  method: InsertMethod;
  targetElement?: string | null;
}): void => {
  const type = variableTypeOf(variable.type);
  track(VARIABLES_EVENTS.INSERT_VARIABLE, {
    "variable type": type,
    "variable source":
      type === "calculation" ? "calculation" : tabForVariable(variable),
    "insert method": method,
    "target element": targetElementOf(targetElement),
    "required plan": requiredPlanForVariable({
      name: bareName(variable.token),
      type: variable.type,
    }),
  });
};

interface ManifestVariable {
  name: string;
  type?: string;
  default?: unknown;
}

/** Derived or read-only: the sender never fills these. */
const NOT_FILLABLE = new Set(["calculation", "system"]);

const hasValue = (value: unknown): boolean => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

/**
 * The variable properties shared by `success publish`, `success create
 * version` and `success update version`, computed one way so the three agree.
 * `data` is the version payload (omit it for a publish); `repeatableKeys` are
 * the story's ROOT repeatable groups, whose rows are counted, not filled.
 */
export const summarizeVariables = (
  manifest: readonly ManifestVariable[],
  data?: Record<string, unknown> | null,
  repeatableKeys: readonly string[] = [],
): Record<string, unknown> => {
  const variables = manifest.filter(
    (v) => v && typeof v.name === "string" && !repeatableKeys.includes(v.name),
  );
  const types = Array.from(
    new Set(variables.map((v) => variableTypeOf(v.type))),
  ).sort();
  const summary: Record<string, unknown> = {
    "variables count": variables.length,
    "variable types": types,
    "has calculation": types.includes("calculation"),
    "has ai": types.includes("ai"),
    "has data binding": types.includes("data") || types.includes("list"),
    "has repeatable": repeatableKeys.length > 0,
    "highest required plan": highestRequiredPlan(
      variables.map((v) => ({
        name: v.name,
        requiredPlan: requiredPlanForVariable(v),
      })),
    ),
  };
  if (!data) return summary;

  const fillable = variables.filter(
    (v) => !NOT_FILLABLE.has(variableTypeOf(v.type)),
  );
  const filled = fillable.filter((v) => hasValue(data[v.name]));
  const optional = data._optionalContent;
  return {
    ...summary,
    "filled variables count": filled.length,
    "fill rate": fillable.length
      ? Math.round((filled.length / fillable.length) * 100) / 100
      : null,
    "defaults used count": filled.filter(
      (v) => hasValue(v.default) && String(data[v.name]) === String(v.default),
    ).length,
    "ai generated count": filled.filter((v) => variableTypeOf(v.type) === "ai")
      .length,
    "repeatable rows count": repeatableKeys.reduce(
      (sum, key) =>
        sum + (Array.isArray(data[key]) ? (data[key] as unknown[]).length : 0),
      0,
    ),
    "optional excluded count":
      optional && typeof optional === "object"
        ? Object.values(optional).filter((included) => included === false)
            .length
        : 0,
  };
};

/** `edit date variable` for a rewritten date pill token (`kickoff+5d|D MMM`). */
export const trackDateVariableEdit = (token: string): void => {
  const pipe = token.indexOf("|");
  const { offset } = parseDateToken(pipe === -1 ? token : token.slice(0, pipe));
  track(VARIABLES_EVENTS.EDIT_DATE_VARIABLE, {
    "has offset": Boolean(offset && offset.amount),
    "has format": pipe !== -1 && token.slice(pipe + 1).trim() !== "",
  });
};

const parseJsonArray = (raw: unknown): Record<string, unknown>[] => {
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  if (typeof raw !== "string" || !raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Record<string, unknown>[]) : [];
  } catch {
    return [];
  }
};

/** Keys of the story's ROOT repeatable groups (`settings.fs.repeatables`). */
export const rootRepeatableKeys = (fs: unknown): string[] => {
  const groups = parseJsonArray(
    (fs as Record<string, unknown> | undefined)?.repeatables,
  );
  const keys = new Set(groups.map((g) => g.key));
  return groups
    .filter((g) => typeof g.key === "string")
    .filter((g) => !g.parentKey || !keys.has(g.parentKey))
    .map((g) => g.key as string);
};

/**
 * `success publish` properties from the published settings (`fs-vars` is the
 * manifest the publish just wrote) and the deck's entities.
 */
export const publishVariablesSummary = (
  settings: { fs?: Record<string, unknown> } | undefined | null,
  entities: Record<string, { settings?: Record<string, unknown> }> = {},
): Record<string, unknown> => {
  const manifest = parseJsonArray(settings?.fs?.["fs-vars"]) as unknown as {
    name: string;
    type?: string;
  }[];
  return {
    ...summarizeVariables(
      manifest,
      undefined,
      rootRepeatableKeys(settings?.fs),
    ),
    "has optional": Object.values(entities).some(
      (entity) => entity?.settings?.optionalConfig,
    ),
  };
};
